import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Bell, MessageSquare, CalendarDays, Check, Clock, User, Inbox, CheckCheck } from "lucide-react";

export default function NotificationsPage() {
  const { authAxios, isAdmin, isSubAdmin, isTeamLead } = useAuth();
  const { refresh: refreshBadges } = useNotifications();
  const navigate = useNavigate();
  const [data, setData] = useState({ notifications: [], pending_notes: [], pending_events: [] });
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const api = authAxios();
      const res = await api.get("/notifications");
      setData(res.data);
    } catch (err) {
      if (err.response?.status !== 403) toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchNotifications(); }, [authAxios]);

  const handleResolveNote = async (noteId) => {
    try {
      const api = authAxios();
      await api.put(`/notes/${noteId}/resolve`);
      toast.success("Note resolved");
      fetchNotifications();
      refreshBadges();
    } catch {
      toast.error("Failed to resolve note");
    }
  };

  const handleMarkRead = async (notifId) => {
    try {
      const api = authAxios();
      await api.put(`/notifications/${notifId}/read`);
      fetchNotifications();
      refreshBadges();
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      const api = authAxios();
      await api.put("/notifications/mark-all-read");
      toast.success("All marked as read");
      fetchNotifications();
      refreshBadges();
    } catch {
      toast.error("Failed");
    }
  };

  const unreadCount = (data.notifications || []).filter((n) => !n.is_read).length;
  const notesCount = data.pending_notes?.length || 0;
  const eventsCount = data.pending_events?.length || 0;
  const canSeeAdmin = isAdmin || isSubAdmin || isTeamLead;

  const NOTIF_COLORS = {
    event_pending: "border-l-amber-500",
    event_approved: "border-l-emerald-500",
    event_rejected: "border-l-red-500",
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 w-full space-y-8" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-foreground flex items-center gap-3">
          <Bell className="h-8 w-8 text-primary" />
          Notifications
          {unreadCount > 0 && <Badge className="bg-red-500 text-white">{unreadCount} new</Badge>}
        </h1>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
            <CheckCheck className="h-4 w-4 mr-2" /> Mark All Read
          </Button>
        )}
      </div>

      <Tabs defaultValue="all" className="w-full" data-testid="notification-tabs">
        <TabsList>
          <TabsTrigger value="all" data-testid="all-tab">
            <Inbox className="h-4 w-4 mr-2" /> All
            {unreadCount > 0 && <Badge className="ml-2 bg-red-500 text-white text-xs h-5 min-w-5 p-0 flex items-center justify-center rounded-full">{unreadCount}</Badge>}
          </TabsTrigger>
          {canSeeAdmin && (
            <TabsTrigger value="notes" data-testid="notes-tab">
              <MessageSquare className="h-4 w-4 mr-2" /> Change Requests
              {notesCount > 0 && <Badge className="ml-2 bg-blue-500 text-white text-xs h-5 min-w-5 p-0 flex items-center justify-center rounded-full">{notesCount}</Badge>}
            </TabsTrigger>
          )}
          {canSeeAdmin && (
            <TabsTrigger value="events" data-testid="events-tab">
              <CalendarDays className="h-4 w-4 mr-2" /> Pending Events
              {eventsCount > 0 && <Badge className="ml-2 bg-amber-500 text-white text-xs h-5 min-w-5 p-0 flex items-center justify-center rounded-full">{eventsCount}</Badge>}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="all" className="mt-6">
          {loading ? (
            <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />)}</div>
          ) : (data.notifications || []).length > 0 ? (
            <div className="space-y-3" data-testid="notifications-list">
              {data.notifications.map((n) => (
                <Card
                  key={n.notif_id}
                  className={`border-border/50 shadow-sm border-l-4 ${NOTIF_COLORS[n.type] || "border-l-primary"} ${n.is_read ? "opacity-60" : ""}`}
                  data-testid={`notif-${n.notif_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{n.title}</span>
                          {!n.is_read && <Badge className="bg-red-500/10 text-red-600 text-[10px]">New</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">{n.message}</p>
                        <p className="text-xs text-muted-foreground">
                          <Clock className="h-3 w-3 inline mr-1" />{new Date(n.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {n.event_id && (
                          <Button size="sm" variant="outline" onClick={() => navigate(`/events/${n.event_id}`)} data-testid={`view-notif-event-${n.notif_id}`}>
                            View
                          </Button>
                        )}
                        {!n.is_read && (
                          <Button size="sm" variant="ghost" onClick={() => handleMarkRead(n.notif_id)} data-testid={`mark-read-${n.notif_id}`}>
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Bell className="h-12 w-12 mx-auto text-muted-foreground/20 mb-3" />
              <p className="text-muted-foreground">No notifications yet</p>
            </div>
          )}
        </TabsContent>

        {canSeeAdmin && (
          <TabsContent value="notes" className="mt-6">
            {data.pending_notes?.length > 0 ? (
              <div className="space-y-3" data-testid="pending-notes-list">
                {data.pending_notes.map((note) => (
                  <Card key={note.note_id} className="border-border/50 shadow-sm border-l-4 border-l-blue-500" data-testid={`note-${note.note_id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="secondary" className="text-xs">{note.event_title}</Badge>
                            <Badge className="bg-blue-500/10 text-blue-600 text-xs"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>
                          </div>
                          <p className="text-sm text-foreground font-medium">{note.message}</p>
                          {note.requested_changes && <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded-md">Changes: {note.requested_changes}</p>}
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <User className="h-3 w-3" /> {note.creator_name} ({note.creator_role}) - {new Date(note.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button size="sm" variant="outline" onClick={() => navigate(`/events/${note.event_id}`)}>View Event</Button>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleResolveNote(note.note_id)} data-testid={`resolve-note-${note.note_id}`}>
                            <Check className="h-4 w-4 mr-1" /> Resolve
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/20 mb-3" />
                <p className="text-muted-foreground">No pending menu change requests</p>
              </div>
            )}
          </TabsContent>
        )}

        {canSeeAdmin && (
          <TabsContent value="events" className="mt-6">
            {data.pending_events?.length > 0 ? (
              <div className="space-y-3" data-testid="pending-events-notifications">
                {data.pending_events.map((event) => (
                  <Card key={event.event_id} className="border-border/50 shadow-sm border-l-4 border-l-amber-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <h3 className="font-medium text-foreground">{event.title}</h3>
                          <p className="text-sm text-muted-foreground">{event.start_date} - {event.end_date}</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => navigate("/approvals")}>Review</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <CalendarDays className="h-12 w-12 mx-auto text-muted-foreground/20 mb-3" />
                <p className="text-muted-foreground">No pending event approvals</p>
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
